import React, { useEffect, useState, useRef } from "react";
import Table from "../../core/pagination/datatable";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useSelector, useDispatch } from "react-redux";
import {
  Trash2,
  Edit,
  PlusCircle,
  RotateCcw,
  ChevronUp,
} from "feather-icons-react/build/IconComponents";
import { secureExportToExcel } from "../../utils/secureExcelUtils";

import PrintDetailsView from "./printdetailsview";
import { getPrintStocks, deletePrint } from "../../core/api/products";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { setToogleHeader } from "../../core/redux/action";

const PrintList = () => {
  const [prints, setPrints] = useState([]);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );
  const selectedClients = useSelector((state) => state.selectedClients);
  const [clientId, setClientId] = useState(null);
  const [isPrintDetailsVisible, setPrintDetailsVisible] = useState(false);
  const [currentPrintId, setCurrentPrintId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const sidebarRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentTableData, setCurrentTableData] = useState([]);
  const [filteredPrints, setFilteredPrints] = useState([]);
  const handleTableChange = (pagination, filters, sorter, extra) => {
    if (extra.currentDataSource) {
      setCurrentTableData(extra.currentDataSource);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filteredData = prints.filter(
      (print) =>
        print.product_name?.toLowerCase().includes(query) ||
        print.code_offer?.toLowerCase().includes(query)
    );

    setFilteredPrints(filteredData);
  };

  const uniqueValues = (data, field) => {
    return [...new Set(data.map((item) => item[field]))]
      .filter(Boolean)
      .map((value) => ({ text: value, value }));
  };

  useEffect(() => {
    fetchPrintData();
  }, [selectedClients]);

  const fetchPrintData = async () => {
    try {
      const response = await getPrintStocks(selectedClients);
      setPrints(response.data || []);
      setFilteredPrints(response.data);
    } catch (error) {
      console.error("Error fetching prints:", error);
      Swal.fire("Error", "Failed to fetch prints.", "error");
    }
  };

  const handleCreatePrint = () => {
    if (selectedClients.length === 0) {
      Swal.fire("Error", "Please select a client.", "error");
      return;
    }

    if (selectedClients.length > 1 || selectedClients[0] === "all") {
      Swal.fire(
        "Error",
        "Please select one client to create a new print record.",
        "error"
      );
      return;
    }
    setClientId(selectedClients[0]);
    setCurrentPrintId(null);
    setEditMode(true);
    setPrintDetailsVisible(true);
  };

  const handleEditPrint = (printId, cId) => {
    setClientId(cId);
    setCurrentPrintId(printId);
    setEditMode(true);
    setPrintDetailsVisible(true);
  };

  const handleClosePrintDetails = () => {
    setPrintDetailsVisible(false);
    setEditMode(false);
    setCurrentPrintId(null);
    setClientId(null);
    fetchPrintData();
  };

  const handleDeletePrint = (printId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePrint(printId);
          Swal.fire(
            "Deleted!",
            "The print record has been deleted.",
            "success"
          );
          fetchPrintData();
        } catch (error) {
          Swal.fire("Error", "Failed to delete the print record.", "error");
        }
      }
    });
  };

  const columns = [
    {
      title: "Brand",
      dataIndex: "brand",
      sorter: (a, b) => (a.brand || "").localeCompare(b.brand || ""),
      filters: uniqueValues(prints, "brand"),
      onFilter: (value, record) => record.brand === value,
      width: 130,
    },
    {
      title: "Product",
      dataIndex: "product_name",
      sorter: (a, b) =>
        (a.product_name || "").localeCompare(b.product_name || ""),
      filters: uniqueValues(prints, "product_name"),
      onFilter: (value, record) => record.product_name === value,
      width: 130,
    },
    {
      title: "Offer Code",
      dataIndex: "code_offer",
      sorter: (a, b) => (a.code_offer || "").localeCompare(b.code_offer || ""),
      filters: uniqueValues(prints, "code_offer"),
      onFilter: (value, record) => record.code_offer === value,
      width: 130,
    },
    
    {
      title: "Printer",
      dataIndex: "printer_name",
      sorter: (a, b) =>
        (a.printer_name || "").localeCompare(b.printer_name || ""),
      filters: uniqueValues(prints, "printer_name"),
      onFilter: (value, record) => record.printer_name === value,
      width: 100,
    },
    {
      title: "Qty Printed",
      dataIndex: "qty_printed",
      sorter: (a, b) => (a.qty_printed || 0) - (b.qty_printed || 0),
      width: 110,
    },
    {
      title: "Total Value Print",
      dataIndex: "total_value_print",
      render: (value) => `$${value?.toFixed(3)}`,
      sorter: (a, b) => (a.total_value_print || 0) - (b.total_value_print || 0),
      width: 110,
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      render: (_, record) => {
        const unitPrice =
          record.qty_printed > 0
            ? record.total_value_print / record.qty_printed
            : 0;
        return `$${unitPrice.toFixed(3)}`;
      },
      sorter: (a, b) => {
        const unitPriceA =
          a.qty_printed > 0 ? a.total_value_print / a.qty_printed : 0;
        const unitPriceB =
          b.qty_printed > 0 ? b.total_value_print / b.qty_printed : 0;
        return unitPriceA - unitPriceB;
      },
      width: 110,
    },
    {
      title: "Total Qty Used",
      dataIndex: "total_qty_used",
      sorter: (a, b) =>
        (a.total_qty_used_cal || 0) - (b.total_qty_used_cal || 0),
      width: 110,
    },
    {
      title: "Stock Remaining",
      dataIndex: "stock_remaining",
      render: (_, record) => {
        const stockRemaining = record.qty_printed - record.total_qty_used;
        return stockRemaining;
      },
      sorter: (a, b) => {
        const stockA = a.qty_printed - a.total_qty_used;
        const stockB = b.qty_printed - b.total_qty_used;
        return stockA - stockB;
      },
      width: 120,
    },
    {
      title: "Value Stock Remaining",
      dataIndex: "value_stock_remaining",
      render: (_, record) => {
        const unitPrice =
          record.qty_printed > 0
            ? record.total_value_print / record.qty_printed
            : 0;
        const stockRemaining = record.qty_printed - record.total_qty_used;
        const valueStockRemaining = stockRemaining * unitPrice;
        return `$${valueStockRemaining.toFixed(3)}`;
      },
      sorter: (a, b) => {
        const unitPriceA =
          a.qty_printed > 0 ? a.total_value_print / a.qty_printed : 0;
        const stockA = a.qty_printed - a.total_qty_used;
        const valueStockA = stockA >= 0 ? stockA * unitPriceA : 0;

        const unitPriceB =
          b.qty_printed > 0 ? b.total_value_print / b.qty_printed : 0;
        const stockB = b.qty_printed - b.total_qty_used;
        const valueStockB = stockB >= 0 ? stockB * unitPriceB : 0;

        return valueStockA - valueStockB;
      },
      width: 120,
    },

    {
      title: "Actual Stock Remaining",
      dataIndex: "actual_stock_remaining",
      sorter: (a, b) =>
        (a.actual_stock_remaining || 0) - (b.actual_stock_remaining || 0),
      width: 120,
    },
    {
      title: "Action",
      dataIndex: "action",
      fixed: "right",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              onClick={() => handleEditPrint(record.id, record.client_id)}
              title="Edit"
            >
              <Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to="#"
              onClick={() => handleDeletePrint(record.id)}
              title="Delete"
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
    },
  ];
  const exportToExcel = () => {
    // Define the export columns
    const exportColumns = [
      { title: "Brand", dataIndex: "brand" },
      { title: "Product", dataIndex: "product_name" },
      { title: "Offer Code", dataIndex: "code_offer" },
      { title: "Printer", dataIndex: "printer_name" },
      { title: "Qty Printed", dataIndex: "qty_printed" },
      {
        title: "Total Value Print",
        dataIndex: "total_value_print",
        format: (value) => `$${value?.toFixed(3)}` ?? "$0.00",
      },
      {
        title: "Stock Remaining",
        calculated: true,
        calculate: (print) => print.qty_printed - print.total_qty_used,
      },
      {
        title: "Value Stock Remaining",
        calculated: true,
        calculate: (print) => {
          const unitPrice =
            print.qty_printed > 0
              ? print.total_value_print / print.qty_printed
              : 0;
          return `$${(
            (print.qty_printed - print.total_qty_used) *
            unitPrice
          ).toFixed(3)}`;
        },
      },
    ];

    const exportData = currentTableData.map((print) => {
      const row = {};
      exportColumns.forEach((col) => {
        if (col.calculated) {
          row[col.title] = col.calculate(print);
        } else if (col.format) {
          row[col.title] = col.format(print[col.dataIndex]);
        } else {
          row[col.title] = print[col.dataIndex] ?? "";
        }
      });
      return row;
    });

    // Create and export the Excel file
    secureExportToExcel(exportData, "print_stock_list.xlsx");
  };

  return (
    <div className="page-wrapper">
      <div
        className="content"
        style={{
          width: isPrintDetailsVisible
            ? `calc(100% - ${sidebarWidth}px)`
            : "100%",
          transition: "width 0.3s ease-in-out",
        }}
      >
        <div className="page-header">
          <div className="page-title">
            <h4>Print Stock</h4>
            <h6>Manage your print stock</h6>
          </div>
          <ul className="table-top-head">
            <div className="search-set">
              <div className="search-input">
                <input
                  type="text"
                  placeholder="Search"
                  className="form-control form-control-sm formsearch"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <Link to="#" className="btn btn-searchset">
                  <i data-feather="search" className="feather-search" />
                </Link>
              </div>
            </div>
            <li>
              <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  onClick={exportToExcel}
                >
                  <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link data-bs-toggle="tooltip" data-bs-placement="top" onClick={fetchPrintData}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch(setToogleHeader(!data));
                  }}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>

          <div className="page-btn" hidden>
            <button className="btn btn-added" onClick={handleCreatePrint}>
              <PlusCircle className="me-2 iconsize" />
              Create New Print Stock
            </button>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={filteredPrints}
                rowKey="id"
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>

      <PrintDetailsView
        ref={sidebarRef}
        isVisible={isPrintDetailsVisible}
        onClose={handleClosePrintDetails}
        printId={currentPrintId}
        onResize={setSidebarWidth}
        setPrintId={setCurrentPrintId}
        fetchPrintList={fetchPrintData}
        clientId={clientId}
      />
    </div>
  );
};

export default PrintList;
